/**
 * v1.0.16 — Reusable "unsaved changes" guard for editable tenant forms.
 *
 * Wires two protections behind a single `dirty` boolean:
 *   1. `beforeunload` — browser refresh / close prompt.
 *   2. React Router in-app navigation — intercepts <Link> clicks and the
 *      sidebar nav. We use a custom approach (history.pushState patch +
 *      anchor click capture) because react-router v6 dropped `<Prompt>`
 *      and `useBlocker` is only available with the data routers, which
 *      this codebase doesn't use.
 *
 * Usage:
 *   const dirty = JSON.stringify(form) !== JSON.stringify(initialForm);
 *   useUnsavedChangesGuard(dirty, "You have unsaved changes. Leave without saving?");
 *
 * The hook is intentionally side-effect heavy but cleans up everything it
 * touches in its return function — calling it twice in different components
 * stacks safely (each call gets its own listener).
 */
import { useEffect } from "react";

const DEFAULT_MSG = "You have unsaved changes. Leave without saving?";

export default function useUnsavedChangesGuard(isDirty, message = DEFAULT_MSG) {
  useEffect(() => {
    if (!isDirty) return undefined;

    // ---- 1. Browser refresh / close ----
    const onBeforeUnload = (e) => {
      // Modern browsers ignore the custom string and show their own copy,
      // but returning a truthy value is the only way to trigger the prompt.
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    // ---- 2. In-app navigation (react-router <Link>) ----
    // We listen at capture-phase on document so we run BEFORE react-router's
    // own handler. If the anchor lives inside the current document and the
    // target route differs from the current pathname, we ask the user.
    const onAnchorClick = (e) => {
      // Only intercept primary-click on a plain left-button anchor.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target?.closest?.("a[href]");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      if (a.target && a.target !== "_self") return;
      // Allow same-pathname clicks (anchor-only links).
      let target;
      try {
        target = new URL(href, window.location.origin);
      } catch {
        return;
      }
      if (target.origin !== window.location.origin) return;
      if (target.pathname === window.location.pathname) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm(message)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener("click", onAnchorClick, true);

    // ---- 3. Browser back / forward ----
    const onPopState = (e) => {
      // Re-prompt; if the user cancels, push the original state back to
      // freeze the URL in place (best-effort — react-router will still
      // see the popstate, but the route guard above catches the next link
      // click). Browsers no longer let JS truly cancel a popstate.
      // eslint-disable-next-line no-alert
      if (!window.confirm(message)) {
        // Push the current pathname back so the URL doesn't visibly change.
        window.history.pushState(null, "", window.location.pathname + window.location.search);
        // Stop react-router from acting on the popped state.
        e.stopImmediatePropagation?.();
      }
    };
    window.addEventListener("popstate", onPopState, true);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onAnchorClick, true);
      window.removeEventListener("popstate", onPopState, true);
    };
  }, [isDirty, message]);
}
